import { useRef } from 'react'
import { Group, Image, Rect } from 'react-konva'
import Konva from 'konva'
import { TileData, MergedTileGroup } from '../types'

interface MergedTileProps {
  group: MergedTileGroup
  tiles: TileData[]
  tileWidth: number
  tileHeight: number
  onDragStart: (groupId: string) => void
  onDragEnd: (groupId: string, x: number, y: number) => void
}

function MergedTile({ group, tiles, tileWidth, tileHeight, onDragStart, onDragEnd }: MergedTileProps) {
  const groupRef = useRef<Konva.Group>(null)

  if (tiles.length === 0) return null

  // Calculate bounding box for the merged group
  const minX = Math.min(...tiles.map(t => t.currentPosition.x))
  const minY = Math.min(...tiles.map(t => t.currentPosition.y))
  const maxX = Math.max(...tiles.map(t => t.currentPosition.x))
  const maxY = Math.max(...tiles.map(t => t.currentPosition.y))

  const groupWidth = maxX - minX + tileWidth
  const groupHeight = maxY - minY + tileHeight

  return (
    <Group
      ref={groupRef}
      x={minX}
      y={minY}
      draggable
      onDragStart={(e) => {
        e.target.moveToTop()
        onDragStart(group.id)
      }}
      onDragEnd={(e) => onDragEnd(group.id, e.target.x(), e.target.y())}
      onMouseEnter={(e) => {
        const container = e.target.getStage()?.container()
        if (container) {
          container.style.cursor = 'grab'
        }
      }}
      onMouseLeave={(e) => {
        const container = e.target.getStage()?.container()
        if (container) {
          container.style.cursor = 'default'
        }
      }}
    >
      {/* Highlight border for merged group */}
      <Rect
        x={-2}
        y={-2}
        width={groupWidth + 4}
        height={groupHeight + 4}
        stroke="#4caf50"
        strokeWidth={3}
        dash={[10, 5]}
        listening={false}
      />

      {/* Render all tiles in the group */}
      {tiles.map(tile => (
        <Image
          key={tile.id}
          x={tile.currentPosition.x - minX}
          y={tile.currentPosition.y - minY}
          width={tileWidth}
          height={tileHeight}
          image={tile.imageData}
          crop={{
            x: tile.sourceX,
            y: tile.sourceY,
            width: tile.sourceWidth,
            height: tile.sourceHeight,
          }}
          shadowBlur={5}
          shadowColor="rgba(0,0,0,0.3)"
          shadowOffsetX={2}
          shadowOffsetY={2}
        />
      ))}
    </Group>
  )
}

export default MergedTile
